import { PredictV2RequestOverride } from "../contracts/overrides/prediction";
import {
  HollowSquareBar,
  HollowTube,
  SolidRoundBar,
  SolidSquareBar,
} from "../contracts/prediction";
import { MaterialLayout, ExtendedPart, PartTypeSchema, PartTypeType } from "../types";
import { DimensionsOverThickness } from "./getOverThicknessFromPartType";
import { isBarOrProfile, isRound, isTube } from "./isBarOrProfile";
import { isSheetPartType } from "./isSheetPartType";

type MaterialLayoutDimensions = Pick<MaterialLayout, "type" | "width" | "height" | "length">;

export type LayoutForCncRawDimensions = {
  useNesting: boolean | null;
  materialLayout: MaterialLayoutDimensions | null;
} | null;

type PartDimensions = Pick<ExtendedPart, "width" | "height" | "length" | "type" | "tubeThickness">;

type CncRawDimensions = PredictV2RequestOverride["cnc_raw_dimensions"];
type RawDimensions = {
  width: number;
  height: number;
  length: number;
  tubeThickness: number | null;
};

type SuccessReturnType = {
  cncRawDimensions: CncRawDimensions;
  rawDimensions: RawDimensions;
  isValid: true;
};

type ErrorReturnType = {
  cncRawDimensions: null;
  rawDimensions: null;
  isValid: false;
  error: string;
};

type GetCncRawDimensionsResult = SuccessReturnType | ErrorReturnType;

const ensurePositive = (
  value: number | null,
): { value: number; isValid: true } | { value: null; isValid: false } =>
  value === null || value <= 0 ? { value: null, isValid: false } : { value, isValid: true };

const ensurePositiveOrNull = (
  value: number | null,
): { value: number | null; isValid: true } | { value: null; isValid: false } => {
  if (value === null) {
    return { value: null, isValid: true };
  }
  return ensurePositive(value);
};

const buildCncRawDimensions = (
  type: PartTypeType | null,
  rawDimensions: RawDimensions,
):
  | { cncRawDimensions: CncRawDimensions; isValid: true }
  | { cncRawDimensions: null; isValid: false; error: string } => {
  switch (type) {
    case PartTypeSchema.enum.ROUND_BAR:
      return {
        cncRawDimensions: {
          raw_type: "solid_round",
          diameter: rawDimensions.width,
          length: rawDimensions.length,
        } satisfies SolidRoundBar,
        isValid: true,
      };
    case PartTypeSchema.enum.ROUND_TUBE: {
      const thicknessResult = ensurePositive(rawDimensions.tubeThickness);
      if (!thicknessResult.isValid) {
        return {
          cncRawDimensions: null,
          isValid: false,
          error: `Tube thickness value is wrong. Got ${rawDimensions.tubeThickness ?? "null"}`,
        };
      }
      return {
        cncRawDimensions: {
          raw_type: "hollow_tube",
          outer_diameter: rawDimensions.width,
          length: rawDimensions.length,
          thickness: thicknessResult.value,
        } satisfies HollowTube,
        isValid: true,
      };
    }
    case PartTypeSchema.enum.SQUARE_TUBE:
    case PartTypeSchema.enum.RECTANGULAR_TUBE: {
      const thicknessResult = ensurePositive(rawDimensions.tubeThickness);
      if (!thicknessResult.isValid) {
        return {
          cncRawDimensions: null,
          isValid: false,
          error: `Tube thickness value is wrong. Got ${rawDimensions.tubeThickness ?? "null"}`,
        };
      }
      return {
        cncRawDimensions: {
          raw_type: "hollow_square",
          width: rawDimensions.width,
          height: rawDimensions.height,
          length: rawDimensions.length,
          thickness: thicknessResult.value,
        } satisfies HollowSquareBar,
        isValid: true,
      };
    }
    case PartTypeSchema.enum.SHEET:
    case PartTypeSchema.enum.MACHINING:
    case PartTypeSchema.enum.SQUARE_BAR:
    case PartTypeSchema.enum.PROFILE:
    default:
      return {
        cncRawDimensions: {
          raw_type: "solid_square",
          width: rawDimensions.width,
          height: rawDimensions.height,
          length: rawDimensions.length,
        } satisfies SolidSquareBar,
        isValid: true,
      };
  }
};

const buildReturn = (
  type: PartTypeType | null,
  rawDimensions: RawDimensions,
): GetCncRawDimensionsResult => {
  const cncRawDimensionsResult = buildCncRawDimensions(type, rawDimensions);
  if (!cncRawDimensionsResult.isValid) {
    return buildErrorReturn(cncRawDimensionsResult.error);
  }
  return {
    cncRawDimensions: cncRawDimensionsResult.cncRawDimensions,
    rawDimensions,
    isValid: true,
  };
};

const buildErrorReturn = (error: string): ErrorReturnType => {
  return {
    cncRawDimensions: null,
    rawDimensions: null,
    isValid: false,
    error,
  };
};

const getMaterialOrPartDimensionValue = ({
  materialLayoutValue,
  partValue,
  overthickness,
  fieldName,
}: {
  materialLayoutValue: number | null | undefined;
  partValue: number | null;
  overthickness: number;
  fieldName: string;
}): { value: number; isValid: true } | { value: null; isValid: false; error: string } => {
  const materialLayoutValueResult = ensurePositiveOrNull(materialLayoutValue ?? null);

  if (!materialLayoutValueResult.isValid) {
    return {
      value: null,
      isValid: false,
      error: `Material layout ${fieldName} is wrong. Got ${materialLayoutValue ?? "null"}`,
    };
  }

  let finalValue = materialLayoutValueResult.value;

  if (finalValue === null) {
    const partValueResult = ensurePositive(partValue);
    if (!partValueResult.isValid) {
      return {
        value: null,
        isValid: false,
        error: `Part ${fieldName} is wrong. Got ${partValue ?? "null"}`,
      };
    }
    finalValue = partValueResult.value + overthickness;
  }
  return { value: finalValue, isValid: true };
};

export const getCncRawDimensions = ({
  layout,
  part,
  overthickness,
}: {
  layout: LayoutForCncRawDimensions;
  part: PartDimensions;
  overthickness: DimensionsOverThickness;
}): GetCncRawDimensionsResult => {
  if (!layout) {
    throw new Error("Layout is required");
  }

  const materialLayout = layout.materialLayout;

  if (!materialLayout) {
    return getCncRawDimensionsFromTypeAndPart(part.type, part, null, overthickness);
  }

  if (layout.useNesting === null) {
    throw new Error("layout.useNesting is required");
  }

  if (layout.useNesting) {
    return getCncRawDimensionsFromTypeAndPart(
      materialLayout.type ?? part.type,
      part,
      materialLayout,
      overthickness,
    );
  }

  const widthResult = ensurePositive(materialLayout.width);
  const lengthResult = ensurePositive(materialLayout.length);
  const heightResult = ensurePositive(materialLayout.height);

  if (!widthResult.isValid || !lengthResult.isValid || !heightResult.isValid) {
    return buildErrorReturn(
      `Material layout dimensions are wrong. Got width: ${materialLayout.width}, length: ${materialLayout.length}, height: ${materialLayout.height}`,
    );
  }

  const partTypeToUse = materialLayout.type ?? part.type;

  const rawDimensions = {
    width: widthResult.value,
    height: isRound(partTypeToUse) ? widthResult.value : heightResult.value,
    length: lengthResult.value,
    tubeThickness: isTube(partTypeToUse) ? part.tubeThickness : null,
  };

  return buildReturn(partTypeToUse, rawDimensions);
};

const getCncRawDimensionsFromTypeAndPart = (
  type: PartTypeType | null,
  part: PartDimensions,
  materialLayout: MaterialLayoutDimensions | null,
  overthickness: DimensionsOverThickness,
): GetCncRawDimensionsResult => {
  if (isRound(type)) {
    const widthResult = getMaterialOrPartDimensionValue({
      materialLayoutValue: materialLayout?.width,
      partValue: part.width,
      overthickness: overthickness.turning.diameter,
      fieldName: "width",
    });

    if (!widthResult.isValid) {
      return buildErrorReturn(widthResult.error);
    }

    const lengthResult = ensurePositive(part.length);

    if (!lengthResult.isValid) {
      return buildErrorReturn(`Part length is wrong. Got ${part.length ?? "null"}`);
    }

    const length = lengthResult.value + overthickness.turning.length;
    return buildReturn(type, {
      width: widthResult.value,
      height: widthResult.value,
      length,
      tubeThickness: part.tubeThickness,
    });
  }

  if (isBarOrProfile(type)) {
    const widthResult = getMaterialOrPartDimensionValue({
      materialLayoutValue: materialLayout?.width,
      partValue: part.width,
      overthickness: overthickness.turning.diameter,
      fieldName: "width",
    });
    if (!widthResult.isValid) {
      return buildErrorReturn(widthResult.error);
    }
    const heightResult = getMaterialOrPartDimensionValue({
      materialLayoutValue: materialLayout?.height,
      partValue: part.height,
      overthickness: overthickness.turning.diameter,
      fieldName: "height",
    });
    if (!heightResult.isValid) {
      return buildErrorReturn(heightResult.error);
    }

    const lengthResult = ensurePositive(part.length);
    if (!lengthResult.isValid) {
      return buildErrorReturn(`Part length is wrong. Got ${part.length ?? "null"}`);
    }
    const length = lengthResult.value + overthickness.turning.length;
    return buildReturn(type, {
      width: widthResult.value,
      height: heightResult.value,
      length,
      tubeThickness: part.tubeThickness,
    });
  }

  if (type && isSheetPartType(type)) {
    const heightResult = getMaterialOrPartDimensionValue({
      materialLayoutValue: materialLayout?.height,
      partValue: part.height,
      overthickness: overthickness.sheet.height,
      fieldName: "height",
    });
    if (!heightResult.isValid) {
      return buildErrorReturn(heightResult.error);
    }

    const widthResult = ensurePositive(part.width);
    if (!widthResult.isValid) {
      return buildErrorReturn(`Part width is wrong. Got ${part.width ?? "null"}`);
    }

    const lengthResult = ensurePositive(part.length);
    if (!lengthResult.isValid) {
      return buildErrorReturn(`Part length is wrong. Got ${part.length ?? "null"}`);
    }

    const width = widthResult.value + overthickness.sheet.width;
    const length = lengthResult.value + overthickness.sheet.length;
    return buildReturn(type, { width, height: heightResult.value, length, tubeThickness: null });
  }

  const heightResult = getMaterialOrPartDimensionValue({
    materialLayoutValue: materialLayout?.height,
    partValue: part.height,
    overthickness: overthickness.machining.height,
    fieldName: "height",
  });
  if (!heightResult.isValid) {
    return buildErrorReturn(heightResult.error);
  }

  const widthResult = ensurePositive(part.width);
  if (!widthResult.isValid) {
    return buildErrorReturn(`Part width is wrong. Got ${part.width ?? "null"}`);
  }

  const lengthResult = ensurePositive(part.length);
  if (!lengthResult.isValid) {
    return buildErrorReturn(`Part length is wrong. Got ${part.length ?? "null"}`);
  }

  const width = widthResult.value + overthickness.machining.width;
  const length = lengthResult.value + overthickness.machining.length;
  return buildReturn(type, { width, height: heightResult.value, length, tubeThickness: null });
};
